import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import useCachedApi, { CacheKeys, CacheTTL } from '../hooks/useCachedApi';
import Navbar from '../student/Navbar';
import AdminSidebar from './AdminSidebar';
import Loader from '../components/Loader';
import { motion } from 'framer-motion';
import {
  FaUserAlt,
  FaUsers,
  FaUserGraduate,
  FaCalendarAlt,
  FaChartLine,
  FaStar,
  FaNewspaper,
  FaArrowRight,
  FaBell,
  FaUserNurse,
  FaUserMd,
  FaFileMedical
} from 'react-icons/fa';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement
} from 'chart.js';

// Website color palette
const websiteColorPalette = {
  primary: '#0077B6',
  secondary: '#20B2AA',
  background: '#FFFFFF',
  text: '#333333',
  accent: '#28A745'
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement
);

const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [counts, setCounts] = useState({
    patients: 0,
    students: 0,
    assistants: 0,
    supervisors: 0,
    appointments: 0,
    reviews: 0,
  });
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [treatmentSheets, setTreatmentSheets] = useState([]);
  const [patients, setPatients] = useState([]);
  const [adminMetrics, setAdminMetrics] = useState({
    studentPerformance: {
      totalStudents: 0,
      activeStudents: 0,
      completionRate: 0
    },
    clinicEfficiency: {
      avgAppointmentsPerDay: 0,
      avgPatientsPerMonth: 0
    }
  });
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { cachedGet } = useCachedApi();
  const univ = user?.university || '';

  useEffect(() => {
    const fetchData = async () => {
      if (!user || !token) {
        setError('Please log in to view your dashboard.');
        setLoading(false);
        return;
      }

      if (!user.university) {
        setError('User profile incomplete. Missing university information.');
        setLoading(false);
        return;
      }

      try {
        const params = { university: univ };
        const [patientsData, studentsData, assistantsData, supervisorsData, appointmentsData, treatmentSheetsData] = await Promise.all([
          cachedGet(`/api/admin/patients?university=${encodeURIComponent(univ)}`, CacheKeys.ADMIN_PATIENTS(univ), CacheTTL.MEDIUM).catch(() => []),
          cachedGet(`/api/admin/students?university=${encodeURIComponent(univ)}`, CacheKeys.ADMIN_STUDENTS(univ), CacheTTL.MEDIUM).catch(() => []),
          cachedGet(`/api/admin/assistants?university=${encodeURIComponent(univ)}`, CacheKeys.ADMIN_ASSISTANTS(univ), CacheTTL.MEDIUM).catch(() => []),
          cachedGet(`/api/admin/supervisors?university=${encodeURIComponent(univ)}`, CacheKeys.ADMIN_SUPERVISORS(univ), CacheTTL.MEDIUM).catch(() => []),
          cachedGet(`/api/admin/appointments?university=${encodeURIComponent(univ)}`, CacheKeys.ADMIN_APPOINTMENTS(univ), CacheTTL.SHORT).catch(() => []),
          cachedGet('/api/admin/treatment-sheets', CacheKeys.ADMIN_TREATMENT_SHEETS(), CacheTTL.MEDIUM).catch(() => []),
        ]);

        const patientsRes = { data: Array.isArray(patientsData) ? patientsData : [] };
        const studentsRes = { data: Array.isArray(studentsData) ? studentsData : [] };
        const assistantsRes = { data: Array.isArray(assistantsData) ? assistantsData : [] };
        const supervisorsRes = { data: Array.isArray(supervisorsData) ? supervisorsData : [] };
        const appointmentsRes = { data: Array.isArray(appointmentsData) ? appointmentsData : [] };
        const treatmentSheetsRes = { data: Array.isArray(treatmentSheetsData) ? treatmentSheetsData : [] };

        const students = studentsRes.data || [];

        let newsRes = { data: [] };
        try {
          newsRes = { data: await cachedGet(`/api/news?university=${encodeURIComponent(univ)}`, CacheKeys.ADMIN_NEWS(univ), CacheTTL.LONG) };
        } catch {
          // continue without news
        }

        let allReviews = [];
        if (students.length > 0) {
          const BATCH = 8;
          for (let i = 0; i < students.length; i += BATCH) {
            const batch = students.slice(i, i + BATCH);
            const reviewResults = await Promise.all(
              batch.map(student => {
                const studentId = student.studentId || student._id;
                if (!studentId) return Promise.resolve([]);
                return cachedGet(`/api/reviews/student?studentId=${studentId}`, CacheKeys.STUDENT_REVIEWS(studentId), CacheTTL.SHORT).catch(() => []);
              })
            );
            reviewResults.forEach(arr => { if (Array.isArray(arr)) allReviews = allReviews.concat(arr); });
          }
        }

        // Remove any duplicate reviews (in case a student appears in multiple queries)
        const uniqueReviews = Array.from(new Map(allReviews.map(review => [review._id, review])).values());

        // Filter out signature storage reviews
        const filteredReviews = uniqueReviews.filter(review =>
          !(review.patientId && review.patientId.nationalId === 'signature-storage')
        );

        // Basic counts
        const countsData = {
          patients: Array.isArray(patientsRes.data) ? patientsRes.data.length : 0,
          students: Array.isArray(studentsRes.data) ? studentsRes.data.length : 0,
          assistants: Array.isArray(assistantsRes.data) ? assistantsRes.data.length : 0,
          supervisors: Array.isArray(supervisorsRes.data) ? supervisorsRes.data.length : 0,
          appointments: Array.isArray(appointmentsRes.data) ? appointmentsRes.data.length : 0,
          reviews: filteredReviews.length,
        };
        
        setCounts(countsData);

        // Set news
        setNews(Array.isArray(newsRes.data) ? newsRes.data : []);

        // Set treatment sheets
        setTreatmentSheets(Array.isArray(treatmentSheetsRes.data) ? treatmentSheetsRes.data : []);

        // Set patients
        setPatients(Array.isArray(patientsRes.data) ? patientsRes.data : []);

        // Calculate and set admin metrics
        const calculatedAdminMetrics = {
          studentPerformance: {
            totalStudents: countsData.students,
            activeStudents: countsData.students, // You can add logic to determine active students
            completionRate: Array.isArray(treatmentSheetsRes.data) && treatmentSheetsRes.data.length > 0 ? 
              ((filteredReviews.length / treatmentSheetsRes.data.length) * 100).toFixed(1) : 0
          },
          clinicEfficiency: {
            avgAppointmentsPerDay: countsData.appointments > 0 ? (countsData.appointments / 30).toFixed(1) : 0, // Assuming 30 days
            avgPatientsPerMonth: Array.isArray(patientsRes.data) && patientsRes.data.length > 0 ? 
              (patientsRes.data.length / 6).toFixed(1) : 0 // Assuming 6 months average
          }
        };
        setAdminMetrics(calculatedAdminMetrics);

        if (
          (Array.isArray(appointmentsRes.data) && appointmentsRes.data.length === 0) &&
          allReviews.length === 0 &&
          (Array.isArray(patientsRes.data) && patientsRes.data.length === 0) &&
          (Array.isArray(treatmentSheetsRes.data) && treatmentSheetsRes.data.length === 0)
        ) {
          // Remove the error message for no data found
          // setError('No data found for your university.');
        }
      } catch (err) {
        const errorMessage =
          err.response?.status === 404
            ? 'Data endpoint not found.'
            : err.response?.status === 401
            ? 'Unauthorized. Please log in again.'
            : err.response?.data?.message || 'Failed to load data';
        setError(errorMessage);
        if (err.response?.status === 401) navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, token, navigate, univ, cachedGet]);



  // Calculate patient trends by month (last 6 months)
  const getPatientTrends = () => {
    const months = [];
    const patientCounts = [];
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthName = date.toLocaleString('default', { month: 'short' });
      months.push(monthName);
      
      // Count patients created in this month
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      
      const count = patients.filter(patient => {
        const createdDate = patient.registrationDate || patient.createdAt;
        if (!createdDate) return false;
        
        try {
          const patientDate = new Date(createdDate);
          return patientDate >= monthStart && patientDate <= monthEnd;
        } catch (e) {
          return false;
        }
      }).length;
      
      patientCounts.push(count);
    }
    
    return { months, patientCounts };
  };

  const { months, patientCounts } = getPatientTrends();

  // Chart data for patient trends
  const patientTrendsData = {
    labels: months,
    datasets: [{
      label: 'New Patients',
      data: patientCounts,
      backgroundColor: 'rgba(0, 119, 182, 0.1)',
      borderColor: '#0077B6',
      borderWidth: 3,
      tension: 0.4,
      fill: true,
      pointBackgroundColor: '#0077B6',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 6,
      pointHoverRadius: 8
    }]
  };

  // Line chart options for patient trends
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#0077B6',
        bodyColor: '#333333',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        bodyFont: { family: "'Inter', sans-serif" },
        titleFont: { family: "'Inter', sans-serif", weight: 'bold' }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: { family: "'Inter', sans-serif", size: 12 }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          borderDash: [2, 4],
          color: 'rgba(0, 0, 0, 0.06)'
        },
        ticks: {
          precision: 0,
          font: { family: "'Inter', sans-serif", size: 12 }
        }
      }
    }
  };


  const container = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  if (loading) return <Loader />;

  return (
    <div className="flex h-screen bg-gray-50">
      <AdminSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-7xl mx-auto">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm"
              >
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <p className="text-red-700 font-medium">{error}</p>
                </div>
              </motion.div>
            )}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
              <div className="mb-6 sm:mb-8">
                <h1 className={`text-2xl sm:text-3xl md:text-4xl font-bold text-[${websiteColorPalette.primary}] mb-1`}>Admin Dashboard</h1>
                <p className={`text-[${websiteColorPalette.text}]`}>Welcome back, {user?.name || 'Admin'}</p>
              </div>
              <motion.div
                variants={container}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="space-y-6 sm:space-y-8"
              >
                {/* Additional Admin Metrics */}
                <motion.div
                  variants={container}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
                >
                  <motion.div
                    variants={item}
                    className="bg-white p-4 sm:p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
                  >
                    <div className="flex items-center">
                      <div className="bg-green-50 w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center mr-3 sm:mr-4">
                        <FaUserGraduate className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-gray-600">Student Performance</p>
                        <p className="text-xl sm:text-2xl font-bold text-green-600">{adminMetrics.studentPerformance.completionRate}%</p>
                        <p className="text-xs text-gray-500">Completion Rate</p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    variants={item}
                    className="bg-white p-4 sm:p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
                  >
                    <div className="flex items-center">
                      <div className="bg-purple-50 w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center mr-3 sm:mr-4">
                        <FaUserNurse className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-gray-600">Clinic Efficiency</p>
                        <p className="text-xl sm:text-2xl font-bold text-purple-600">{adminMetrics.clinicEfficiency.avgPatientsPerMonth}</p>
                        <p className="text-xs text-gray-500">Avg Patients/Month</p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    variants={item}
                    className="bg-white p-4 sm:p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
                  >
                    <div className="flex items-center">
                      <div className="bg-orange-50 w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center mr-3 sm:mr-4">
                        <FaCalendarAlt className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-gray-600">Daily Activity</p>
                        <p className="text-xl sm:text-2xl font-bold text-orange-600">{adminMetrics.clinicEfficiency.avgAppointmentsPerDay}</p>
                        <p className="text-xs text-gray-500">Avg Appts/Day</p>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    variants={item}
                    className="bg-white p-4 sm:p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
                  >
                    <div className="flex items-center">
                      <div className="bg-blue-50 w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center mr-3 sm:mr-4">
                        <FaUsers className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-gray-600">Team Size</p>
                        <p className="text-xl sm:text-2xl font-bold text-blue-600">{counts.students + counts.assistants + counts.supervisors}</p>
                        <p className="text-xs text-gray-500">Total Staff</p>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>


                {/* Trends Chart */}
                <motion.div
                  variants={item}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 overflow-hidden"
                >
                  <div className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-4">
                      <h2 className={`text-lg sm:text-xl font-bold text-[${websiteColorPalette.primary}] flex items-center`}>
                        <FaChartLine className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                        Growth Trends & Analytics
                      </h2>
                      <Link
                        to="/admin/analytics"
                        className={`px-3 sm:px-4 py-2 bg-[${websiteColorPalette.primary}] text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center text-sm`}
                      >
                        <span>View Detailed Analytics</span>
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </Link>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                      <div className="bg-blue-50 p-4 sm:p-6 rounded-lg">
                        <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-3">Patient Growth Trends</h3>
                        <p className="text-sm text-gray-600 mb-4">New patient registrations over the last 6 months</p>
                        <div className="h-64 sm:h-80">
                          <Line data={patientTrendsData} options={lineChartOptions} />
                        </div>
                      </div>
                      <div className="bg-blue-50 p-4 sm:p-6 rounded-lg">
                        <h3 className="text-base sm:text-lg font-medium text-gray-700 mb-3">Trend Analysis</h3>
                        <div className="space-y-4">
                          <div className="bg-white p-4 rounded-lg">
                            <h4 className="font-medium text-gray-800 mb-2">Monthly Growth Rate</h4>
                            <div className="flex items-center justify-between">
                              <span className="text-2xl font-bold text-green-600">
                                {patientCounts.length > 1 ? 
                                  ((patientCounts[patientCounts.length - 1] - patientCounts[patientCounts.length - 2]) / Math.max(patientCounts[patientCounts.length - 2], 1) * 100).toFixed(1)
                                  : 0
                                }%
                              </span>
                              <span className="text-sm text-gray-500">vs last month</span>
                            </div>
                          </div>
                          <div className="bg-white p-4 rounded-lg">
                            <h4 className="font-medium text-gray-800 mb-2">Average Monthly Patients</h4>
                            <div className="flex items-center justify-between">
                              <span className="text-2xl font-bold text-blue-600">
                                {patientCounts.length > 0 ? (patientCounts.reduce((a, b) => a + b, 0) / patientCounts.length).toFixed(1) : 0}
                              </span>
                              <span className="text-sm text-gray-500">patients/month</span>
                            </div>
                          </div>
                          <div className="bg-white p-4 rounded-lg">
                            <h4 className="font-medium text-gray-800 mb-2">Total New Patients</h4>
                            <div className="flex items-center justify-between">
                              <span className="text-2xl font-bold text-purple-600">
                                {patientCounts.reduce((a, b) => a + b, 0)}
                              </span>
                              <span className="text-sm text-gray-500">last 6 months</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;